/**
 * Soft≠Live Page193 — Fleet Group detail residual liquid (page139/72 + status dual).
 * Mount: /fleet/group/{name}
 * Buses:
 *   fleet group detail status (always-on · dual members ∥ settings/sound)
 *   devices register · membership write
 *   group-cards settings · siren arm
 * Soft residual deleted:
 *  - sandpit GroupPage without rail / KPI / posture
 *  - silent register invent
 *  - ghost members when register fails (honest blank + error)
 *  - settings/siren invent when write seat sealed
 */
import { useEffect, useMemo, useState } from "react";
import { html, Ico, KV, Card, Field, Alert, Ticker, go, canonicalGroupKey, sameGroupKey } from "./ui.js";
import { path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { boardLabelSay } from "./operator-say.js";

/** Soft operator errors — never paint wire paths. */
export function softFleetGroupsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|insufficient|not_authorized|device not in|cannot change|operation denied/.test(low)) {
    return "Your account cannot change this group. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found/.test(low)) {
    return "That unit or group is not available.";
  }
  if (/\b400\b|invalid|required/.test(low)) {
    return "That change was not accepted.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|offline|revoked/.test(low)) {
    return "This unit is not open to commands right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/devices\/|\/fleet\/|\/group|http|GET |POST |PATCH |PUT |status_path/.test(low)) {
    return "That action could not be completed.";
  }
  return "That action could not be completed.";
}


const KINDS = {
  camera:  { t: "Camera", glyph: "camera", tone: "#3DE1C8" },
  contact: { t: "Door contact", glyph: "door", tone: "#7FD1FF" },
  siren:   { t: "Siren", glyph: "siren", tone: "#FF9B5C" },
  gateway: { t: "Mast / gateway", glyph: "mast", tone: "#A78BFA" },
  sensor:  { t: "Sensor", glyph: "sensor", tone: "#9BE37D" },
  lidar:   { t: "Lidar", glyph: "lidar", tone: "#FFD166" },
  node:    { t: "Node", glyph: "chip", tone: "#8CA2A6" },
};
const kindOf = (k) => KINDS[k] || KINDS.node;

/** Soft≠Live Page193: missing contract ops must not crash the desk. */
function safeGroupPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    return "";
  }
}

/** Pure: seat chips from Live Fleet Group detail posture (Page193). */
export function fleetGroupDetailSeatOf(post, opts = {}) {
  const empty = {
    membersRegisterAllowed: false,
    membershipWriteAllowed: false,
    settingsAllowed: false,
    soundAllowed: false,
    reReadAllowed: false,
    groupsNavAllowed: false,
    zonesNavAllowed: false,
    role: String(opts.role || ""),
    detail: opts.loading ? "fleet_group_detail_status_loading" : "fleet_group_detail_status_error",
    detailLabel: opts.loading ? "Confirming group seat…" : "Could not confirm group access",
    /* Fail-closed: loading never invents an open shell. */
    enabled: false,
    membersPaintMax: 64,
    outsidePaintMax: 24,
    sirensSoundMax: 20,
    postureLabel: null,
  };
  if (opts.loading || opts.error) return empty;
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const mMax = Number(lim.members_paint_max);
  const oMax = Number(lim.outside_paint_max);
  const sMax = Number(lim.sirens_sound_max);
  return {
    membersRegisterAllowed: p.members_register_allowed === true,
    membershipWriteAllowed: p.membership_write_allowed === true,
    settingsAllowed: p.settings_allowed === true,
    soundAllowed: p.sound_allowed === true,
    soundClaimGreen: p.sound_claim_green === true,
    soundOutcomeR3Ready: p.sound_outcome_r3_ready === true,
    reReadAllowed: p.re_read_allowed === true,
    groupsNavAllowed: p.groups_nav_allowed === true,
    zonesNavAllowed: p.zones_nav_allowed === true,
    role: String(p.principal_role || opts.role || ""),
    detail: p.detail || null,
    detailLabel: p.posture_label || null,
    enabled: p.enabled === true,
    membersPaintMax: Number.isFinite(mMax) && mMax > 0 ? Math.floor(mMax) : 64,
    outsidePaintMax: Number.isFinite(oMax) && oMax > 0 ? Math.floor(oMax) : 24,
    sirensSoundMax: Number.isFinite(sMax) && sMax > 0 ? Math.floor(sMax) : 20,
    postureLabel: p.posture_label || null,
  };
}

function guessKind(row) {
  const blob = [
    row && row.display_label, row && row.map_label, row && row.hw_code_resolved,
    row && row.product_hw_code, row && row.board_profile, row && row.chip_target,
  ].filter(Boolean).join(" ").toLowerCase();
  if (/\b(cam|camera|snap|vision|lidar)\b/.test(blob)) return /lidar/.test(blob) ? "lidar" : "camera";
  if (/\b(siren|strobe|horn|alarm)\b/.test(blob)) return "siren";
  if (/\b(door|contact|reed|magnet)\b/.test(blob)) return "contact";
  if (/\b(mast|gateway|bridge|hub)\b/.test(blob)) return "gateway";
  if (/\b(sensor|leak|smoke|pir|temp)\b/.test(blob)) return "sensor";
  return "node";
}

/** Pure: map devices register → group desk rows. Offline never invented. */
export function fleetGroupPageDevicesOf(devicesPack) {
  const rows = (devicesPack && (devicesPack.items || devicesPack.devices)) || [];
  if (!Array.isArray(rows)) return [];
  const mapped = rows.map((r) => {
    if (!r) return null;
    const id = String(r.device_id || "").trim();
    if (!id) return null;
    const online = !!(r.is_online === true || r.online === true || r.presence === "online"
      || String(r.status || "").toLowerCase() === "online");
    return {
      id,
      label: boardLabelSay(r, id),
      kind: guessKind(r),
      zone: String(r.zone || "").trim(),
      group: String(r.notification_group || "").trim(),
      online,
      owner_admin: String(r.owner_admin || r.tenant_owner || r.owner || "").trim(),
    };
  }).filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const d of mapped) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    out.push(d);
  }
  return out;
}

/** Pure: unique Live owners for a named group's members (SA multi-tenant seat). */
export function fleetGroupOwnersOf(members) {
  const list = Array.isArray(members) ? members : [];
  return [...new Set(list.map((d) => d && d.owner_admin).filter(Boolean))].sort();
}

/** Pure: role text when group is fanned out — composition, not invention. */
export function roleOnFireOf(kind) {
  if (kind === "siren") return { t: "sounds", say: "for the dwell set below", tone: "go" };
  if (kind === "camera") {
    return {
      t: "holds a frame only if it is the trigger",
      say: "siblings sound; they do not capture",
      tone: "",
    };
  }
  if (kind === "gateway") return { t: "relays", say: "carries the fan-out to the segment", tone: "" };
  if (kind === "contact" || kind === "door") {
    return { t: "reports", say: "state is read, nothing is driven", tone: "" };
  }
  return { t: "reports", say: "contributes an observation and takes no action", tone: "" };
}

/** Pure: KPI strip for one named group. */
export function fleetGroupPageKpisOf(members) {
  const list = Array.isArray(members) ? members : [];
  const sirens = list.filter((d) => d && d.kind === "siren").length;
  const quiet = list.filter((d) => d && !d.online).length;
  const zones = new Set(list.map((d) => d && d.zone).filter(Boolean)).size;
  return { members: list.length, sirens, quiet, zones };
}

/** Pure: hydrate dwell/delay from Live settings pack. */
export function fleetGroupSettingsOf(row) {
  if (!row || typeof row !== "object") return null;
  return {
    dwell: Math.max(1, Math.round((Number(row.trigger_duration_ms) || 8000) / 1000)),
    delay: Math.max(0, Number(row.delay_seconds) || 0),
    reboot_self_check: !!row.reboot_self_check,
  };
}

const Num = ({ v, set, step, min, max, unit, disabled }) => html`
  <div className="btn-row" style=${{ gap: 6, alignItems: "center" }}>
    <button type="button" className="btn btn--ghost btn--sm" disabled=${!!disabled}
      onClick=${() => set(Math.max(min, v - step))}><${Ico} d="minus" w=${12}/></button>
    <b style=${{ fontFamily: "var(--mono)", minWidth: 70, textAlign: "center" }}>${v} ${unit}</b>
    <button type="button" className="btn btn--ghost btn--sm" disabled=${!!disabled}
      onClick=${() => set(Math.min(max, v + step))}><${Ico} d="plus" w=${12}/></button>
  </div>`;

export function FleetGroupDesk({ name, toast }) {
  const gname = decodeURIComponent(name || "");
  const gkey = canonicalGroupKey(gname) || gname;
  const posture = useApi(safeGroupPath("get__fleet_group_status"));
  const post = posture.data || {};
  const seat = fleetGroupDetailSeatOf(post, {
    loading: !!posture.loading,
    error: !!posture.error,
  });
  const devicesApi = useApi(seat.membersRegisterAllowed ? safeGroupPath("get__devices") : "");
  const act = useAction();
  const envelope = act.envelope;
  const register = useMemo(() => fleetGroupPageDevicesOf(devicesApi.data), [devicesApi.data]);
  const [devs, setDevs] = useState([]);
  const [dwell, setDwell] = useState(8);
  const [delay, setDelay] = useState(15);
  const [saved, setSaved] = useState({ dwell: 8, delay: 15 });
  const [firing, setFiring] = useState(false);
  const [ownerSeat, setOwnerSeat] = useState("");

  useEffect(() => { setDevs(register.map((d) => ({ ...d }))); }, [register]);

  const members = useMemo(
    () => devs.filter((d) => sameGroupKey(d.group, gname)).slice(0, seat.membersPaintMax),
    [devs, gname, seat.membersPaintMax],
  );
  const owners = useMemo(() => fleetGroupOwnersOf(members), [members]);
  useEffect(() => {
    if (!owners.length) {
      if (ownerSeat) setOwnerSeat("");
      return;
    }
    if (!ownerSeat || !owners.includes(ownerSeat)) setOwnerSeat(owners[0]);
  }, [owners, ownerSeat]);

  const settingsPath = useMemo(() => {
    if (!gkey || !seat.settingsAllowed) return null;
    const q = {};
    if (ownerSeat) q.owner_admin = ownerSeat;
    try {
      return path("get__group-cards_p_settings", {
        pathParams: [gkey],
        query: Object.keys(q).length ? q : undefined,
      });
    } catch {
      return "";
    }
  }, [gkey, ownerSeat, seat.settingsAllowed]);
  const settings = useApi(settingsPath);
  useEffect(() => {
    const next = fleetGroupSettingsOf(settings.data);
    if (!next) return;
    setDwell(next.dwell);
    setDelay(next.delay);
    setSaved({ dwell: next.dwell, delay: next.delay });
  }, [settings.data]);

  const outside = useMemo(
    () => devs.filter((d) => !sameGroupKey(d.group, gname)).slice(0, seat.outsidePaintMax),
    [devs, gname, seat.outsidePaintMax],
  );
  const zones = useMemo(
    () => [...new Set(members.map((d) => d.zone))].filter(Boolean),
    [members],
  );
  const sirens = useMemo(() => members.filter((d) => d.kind === "siren"), [members]);
  const kpis = useMemo(() => fleetGroupPageKpisOf(members), [members]);
  const dirty = dwell !== saved.dwell || delay !== saved.delay;
  const err = devicesApi.error || settings.error || posture.error;
  const knownEmpty = !gname
    || (!members.length && !register.some((d) => sameGroupKey(d.group, gname)) && !devicesApi.loading
      && seat.membersRegisterAllowed);

  const reRead = () => {
    posture.reload && posture.reload();
    if (seat.membersRegisterAllowed) devicesApi.reload && devicesApi.reload();
    if (seat.settingsAllowed) settings.reload && settings.reload();
    toast && toast("Group status refreshed", "go");
  };

  const move = async (id, group) => {
    if (!seat.membershipWriteAllowed) {
      toast && toast("Membership changes closed for this seat", "warn");
      return;
    }
    const next = group === "Unassigned" || !group ? "" : canonicalGroupKey(group) || group;
    const out = await act.run(path("patch__devices_p_profile", { pathParams: [id] }), {
      method: "PATCH",
      body: { notification_group: next },
    });
    if (!out) {
      toast && toast(softFleetGroupsErrorOf(act.error), "bad");
      return;
    }
    setDevs((v) => v.map((d) => (d.id === id ? { ...d, group: next } : d)));
    toast && toast(
      sameGroupKey(next, gname)
        ? ("Moved into " + gname)
        : ("Removed from " + gname),
      "go",
    );
    devicesApi.reload && devicesApi.reload();
  };

  const test = async () => {
    if (!seat.soundAllowed) {
      toast && toast("Sound controls closed for this seat", "warn");
      return;
    }
    if (!sirens.length) {
      toast && toast("Nothing in this group can sound", "warn");
      return;
    }
    setFiring(true);
    try {
      const applyQuery = ownerSeat ? { owner_admin: ownerSeat } : undefined;
      const out = await act.run(
        path("post__group-cards_p_apply", {
          pathParams: [gkey],
          query: applyQuery,
        }),
        { method: "POST", body: {} },
      );
      if (out) {
        const n = out.device_count != null ? out.device_count : out.sent_now;
        const green = out.sound_claim_green === true || out.physical_observed === true;
        const stage = String(out.outcome_stage || "command_sent");
        toast && toast(
          green
            ? ("Sounding group · " + gname + (n != null ? " · devices " + n : "") + " · " + dwell + " s"
              + (out.acked_n != null ? (" · ACK " + out.acked_n) : ""))
            : ("Command sent · awaiting board ACK · " + gname
              + (n != null ? " · devices " + n : "")
              + (out.acked_n != null ? (" · ACK " + out.acked_n) : "")
              + " · " + stage),
          green ? "go" : "warn",
        );
      } else {
        toast && toast(softFleetGroupsErrorOf(act.error), "bad");
      }
    } finally {
      setTimeout(() => setFiring(false), Math.min(dwell, 6) * 1000);
    }
  };

  const saveSettings = async () => {
    if (!seat.settingsAllowed) {
      toast && toast("Settings closed for this seat", "warn");
      return;
    }
    const body = {
      trigger_mode: delay > 0 ? "delay" : "continuous",
      trigger_duration_ms: dwell * 1000,
      delay_seconds: delay,
      reboot_self_check: !!(settings.data && settings.data.reboot_self_check),
    };
    if (ownerSeat) body.owner_admin = ownerSeat;
    const putPath = path("put__group-cards_p_settings", {
      pathParams: [gkey],
      query: ownerSeat ? { owner_admin: ownerSeat } : undefined,
    });
    const out = await act.run(putPath, { method: "PUT", body });
    if (!out) {
      toast && toast(softFleetGroupsErrorOf(act.error), "bad");
      return;
    }
    setSaved({ dwell, delay });
    toast && toast("Group settings saved · " + gname, "go");
    settings.reload && settings.reload();
  };

  if (knownEmpty) {
    return html`<div className="fleet-group-desk fleet-group-desk--liquid"
      data-cmc-page="page139-group" data-cmc-legacy="page72-group" data-cmc-residual="page193-group">
      <${Alert} tone="bad" icon="ban" title=${"No group named " + (gname || "—")}
        say="A group exists only while a unit still carries its name on this account."/>
      <div className="btn-row fleet-group-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" disabled=${!seat.groupsNavAllowed}
          onClick=${() => go("/machine/groups")}>
          All groups</button>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead} disabled=${!seat.reReadAllowed}>
          <${Ico} d="refresh" w=${14}/> Refresh</button>
      </div>
    </div>`;
  }

  return html`<div className="fleet-group-desk fleet-group-desk--liquid"
    data-cmc-page="page139-group" data-cmc-legacy="page72-group" data-cmc-residual="page193-group">
    <div className="fleet-group-desk__rail">
      <div>
        <p className="eyebrow">Fleet · Group</p>
        <h2 className="fleet-group-desk__title">${gname}</h2>
        <p className="note">
          Members, settings, and sound stay independent. Empty seats stay empty.
        </p>
      </div>
      <div className="btn-row fleet-group-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
          disabled=${devicesApi.loading || settings.loading || posture.loading || !seat.reReadAllowed}>
          <${Ico} d="refresh" w=${14}/> Refresh
        </button>
        <button type="button" className="btn btn--ghost btn--sm" disabled=${!seat.groupsNavAllowed}
          onClick=${() => go("/machine/groups")}>All groups</button>
        <button type="button" className="btn btn--ghost btn--sm" disabled=${!seat.zonesNavAllowed}
          onClick=${() => go("/fleet/zones")}>Zones</button>
      </div>
    </div>

    <${Alert} tone="info" title="Group detail"
      say="Members, settings, and sound are separate gates. Empty stays empty until the group seat answers."/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <div className="fleet-group-desk__kpis" role="group" aria-label="Group posture KPIs">
      <div className="fleet-group-desk__kpi" data-on=${seat.membersRegisterAllowed ? "1" : "0"}>
        <span>Members</span>
        <b>${seat.membersRegisterAllowed ? "open" : "sealed"}</b>
        <em>register</em>
      </div>
      <div className="fleet-group-desk__kpi" data-on=${seat.settingsAllowed ? "1" : "0"}>
        <span>Settings</span>
        <b>${seat.settingsAllowed ? "open" : "sealed"}</b>
        <em>independent</em>
      </div>
      <div className="fleet-group-desk__kpi" data-on=${seat.soundAllowed ? "1" : "0"}>
        <span>Sound</span>
        <b>${seat.soundAllowed ? "open" : "sealed"}</b>
        <em>sirens</em>
      </div>
      <div className="fleet-group-desk__kpi" data-on="0">
        <span>Seat</span>
        <b>${seat.role || "—"}</b>
        <em>seat</em>
      </div>
    </div>
    ${seat.detailLabel ? html`<p className="note">${seat.detailLabel}</p>` : null}

    ${err ? html`
      <${Alert} tone="warn" title="Group partly unavailable"
        say=${softFleetGroupsErrorOf(err)}/>` : null}

    <div className="fleet-group-desk__kpis" role="group" aria-label="Group KPIs">
      ${[
        ["Members", kpis.members, kpis.members ? "1" : "0", ""],
        ["Can sound", kpis.sirens, kpis.sirens ? "1" : "0", ""],
        ["Not answering", kpis.quiet, kpis.quiet ? "1" : "0", "off"],
        ["Zones", kpis.zones, kpis.zones ? "1" : "0", ""],
      ].map(([k, v, on, tone]) => html`
        <div key=${k} className="fleet-group-desk__kpi" data-on=${on} data-tone=${tone}>
          <span>${k}</span>
          <b><${Ticker} value=${v}/></b>
          <em>Device register</em>
        </div>`)}
    </div>

    ${owners.length > 1 ? html`
      <div className="chips" style=${{ marginBottom: 4 }}>
        ${owners.map((o) => html`
          <button key=${o} type="button" className="chip" data-tone="ai"
            data-sel=${ownerSeat === o ? "1" : "0"}
            onClick=${() => setOwnerSeat(o)}>owner · ${o}</button>`)}
      </div>
      <p className="note" style=${{ margin: "0 0 10px" }}>
        This name spans more than one owner — pick whose settings you are editing.
      </p>` : null}
    ${owners.length === 1 && ownerSeat ? html`
      <p className="note" style=${{ margin: "0 0 8px" }}>settings owner · ${ownerSeat}</p>` : null}

    <div className="dhead glass fleet-group-desk__head">
      <span className="dhead__ic" style=${{ "--kc": "var(--aqua)" }}><${Ico} d="grid" w=${20}/></span>
      <span className="dhead__t">
        <h1 style=${{ margin: 0, fontSize: "var(--t-lead)" }}>${gname}</h1>
        <em>group · ${members.length} device${members.length === 1 ? "" : "s"} ·
          ${zones.length ? zones.join(" · ") : "no zone"}</em>
      </span>
      <span className="dhead__st" data-off=${sirens.length ? "0" : "1"}>
        <i/>${sirens.length ? sirens.length + " can sound" : "nothing can sound"}</span>
    </div>

    <div className="grid g2">
      <${Card} title="What happens when this group fires" meta=${members.length + " get their own command"}>
        <div className="mlist">
          ${members.map((d) => {
            const r = roleOnFireOf(d.kind);
            const off = !d.online;
            return html`
              <button key=${d.id} type="button" className="mrow mrow--add"
                onClick=${() => go("/fleet/device/" + d.id)}>
                <span className="chip" style=${{
                  "--kc": kindOf(d.kind).tone,
                  ...(firing && d.kind === "siren" ? { boxShadow: "0 0 0 2px var(--amber)" } : null),
                }}>
                  <${Ico} d=${kindOf(d.kind).glyph} w=${13}/></span>
                <span className="mrow__t"><b>${d.label}</b><em>${r.t} — ${r.say}</em></span>
                ${off && html`<em className="grp__off">not answering</em>`}
              <//>`;
          })}
          ${!members.length ? html`<p className="note">No members yet — add below then refresh.</p>` : null}
        </div>
        ${sirens.length === 0 && html`
          <${Alert} tone="warn" icon="alert" title="A trigger here notifies and nothing sounds"
            say="No siren in this group — add a siren unit if this group must be heard."/>`}
        <div className="btn-row" style=${{ marginTop: 14 }}>
          <button type="button" className="btn btn--go btn--sm"
            disabled=${firing || !sirens.length || act.busy || !seat.soundAllowed} onClick=${test}>
            <${Ico} d="siren" w=${13}/> ${firing ? "Sounding…" : "Sound this group"}</button>
        </div>
      <//>

      <${Card} title="How long and how soon" meta="Group settings"
          sub="Dwell and delay apply for every member. No silent per-unit override invent.">
        <${Field} label="Dwell" hint="How long a siren stays on once it starts.">
          <${Num} v=${dwell} step=${1} min=${1} max=${300} unit="s" set=${setDwell}
            disabled=${!seat.settingsAllowed}/>
        <//>
        <${Field} label="Delay before sounding"
            hint="Zero sounds immediately. Above zero gives time to cancel.">
          <${Num} v=${delay} step=${5} min=${0} max=${600} unit="s" set=${setDelay}
            disabled=${!seat.settingsAllowed}/>
        <//>
        <div className="g2" style=${{ marginTop: 4 }}>
          <${KV} k="Can sound"
            v=${sirens.length ? sirens.length + " of " + members.length : "none"}
            tone=${sirens.length ? "hi" : "bad"}/>
          <${KV} k="Not answering" v=${kpis.quiet + " of " + members.length}/>
        </div>
        <div className="btn-row" style=${{ marginTop: 14 }}>
          <button type="button" className="btn btn--go btn--sm" disabled=${!dirty || act.busy || !seat.settingsAllowed}
            onClick=${saveSettings}>
            ${dirty ? "Save settings" : "Settings saved"}</button>
          <button type="button" className="btn btn--ghost btn--sm" disabled=${!dirty}
            onClick=${() => { setDwell(saved.dwell); setDelay(saved.delay); }}>
            Discard draft</button>
        </div>
      <//>
    </div>

    <${Card} title="Membership" meta="Group membership"
        sub="Rewrites one field on each unit you can control.">
      <div className="mlist">
        ${members.map((d) => html`
          <div key=${d.id} className="mrow">
            <span className="chip" style=${{ "--kc": kindOf(d.kind).tone }}>
              <${Ico} d=${kindOf(d.kind).glyph} w=${13}/></span>
            <span className="mrow__t"><b>${d.label}</b><em>${d.zone || "—"} · ${d.id}</em></span>
            <button type="button" className="mrow__x" title="Remove from group"
              disabled=${!seat.membershipWriteAllowed}
              onClick=${() => move(d.id, "Unassigned")}><${Ico} d="close" w=${12}/></button>
          </div>`)}
        ${outside.map((d) => html`
          <button key=${d.id} type="button" className="mrow mrow--add"
            disabled=${!seat.membershipWriteAllowed}
            onClick=${() => move(d.id, gname)}>
            <span className="chip" style=${{ "--kc": kindOf(d.kind).tone }}>
              <${Ico} d=${kindOf(d.kind).glyph} w=${13}/></span>
            <span className="mrow__t"><b>${d.label}</b><em>${d.zone || "—"} · ${d.group || "unassigned"}</em></span>
            <${Ico} d="plus" w=${13}/>
          <//>`)}
        ${!members.length && !outside.length ? html`
          <p className="note">Register empty or refused — no invented members.</p>` : null}
      </div>
    <//>
  </div>`;
}

/** Back-compat mount name used by fleet.js route solo. */
export function GroupPage({ name, toast }) {
  return html`<${FleetGroupDesk} name=${name} toast=${toast}/>`;
}
